import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { rentableItemService } from '../../services/api';
import '../../styles/common.css';

const RentableItemForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    deposit: '',
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const loadItem = useCallback(async () => {
    try {
      setLoading(true);
      const result = await rentableItemService.getById(id);
      if (result.success) {
        setFormData({
          name: result.data.name,
          description: result.data.description || '',
          price: result.data.price.toString(),
          deposit: result.data.deposit.toString(),
          isActive: result.data.isActive
        });
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Errore nel caricamento dell\'oggetto');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadItem();
    }
  }, [isEdit, loadItem]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };

  const validate = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Il nome è obbligatorio';
    }

    if (!formData.price || parseFloat(formData.price) < 0) {
      errors.price = 'Il prezzo deve essere maggiore o uguale a zero';
    }

    if (!formData.deposit || parseFloat(formData.deposit) < 0) {
      errors.deposit = 'La cauzione deve essere maggiore o uguale a zero';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const result = isEdit
        ? await rentableItemService.update(id, formData)
        : await rentableItemService.create(formData);

      if (result.success) {
        navigate('/rentable-items');
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Errore nel salvataggio dell\'oggetto');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12 col-lg-8 mx-auto">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>{isEdit ? 'Modifica Oggetto' : 'Nuovo Oggetto Noleggiabile'}</h1>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate('/rentable-items')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Torna alla lista
            </button>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label htmlFor="name" className="form-label">
                      Nome <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.name ? 'is-invalid' : ''}`}
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Es: Scarpe da ginnastica"
                      required
                    />
                    {validationErrors.name && (
                      <div className="invalid-feedback">{validationErrors.name}</div>
                    )}
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="description" className="form-label">
                      Descrizione
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Descrizione dettagliata dell'oggetto..."
                    ></textarea>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="price" className="form-label">
                      Prezzo Noleggio (€) <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">€</span>
                      <input
                        type="number"
                        className={`form-control ${validationErrors.price ? 'is-invalid' : ''}`}
                        id="price"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        required
                      />
                      {validationErrors.price && (
                        <div className="invalid-feedback">{validationErrors.price}</div>
                      )}
                    </div>
                    <small className="form-text text-muted">
                      Prezzo per l'intero periodo di noleggio
                    </small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="deposit" className="form-label">
                      Cauzione (€) <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">€</span>
                      <input
                        type="number"
                        className={`form-control ${validationErrors.deposit ? 'is-invalid' : ''}`}
                        id="deposit"
                        name="deposit"
                        value={formData.deposit}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        required
                      />
                      {validationErrors.deposit && (
                        <div className="invalid-feedback">{validationErrors.deposit}</div>
                      )}
                    </div>
                    <small className="form-text text-muted">
                      Cauzione richiesta per il noleggio
                    </small>
                  </div>

                  <div className="col-md-12 mb-4">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Oggetto attivo e disponibile per il noleggio
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate('/rentable-items')}
                    disabled={loading}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Salvataggio...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        {isEdit ? 'Aggiorna' : 'Crea'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentableItemForm;
